import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import { useAuth } from "../context/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import SiteImage from "./SiteImage";
import html2canvas from "html2canvas";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import LoadingPage from "./LoadingPage";

// Fix Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const EditSite = () => {
  const { accessToken } = useAuth();
  const { id } = useParams(); // Get siteId from URL
  const navigate = useNavigate(); // For redirecting
  const baseURL = process.env.REACT_APP_BASEURL;
  const mapRef = useRef(null);

  // State
  const [siteName, setSiteName] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [emailNotifications, setEmailNotifications] = useState(false);
  const [mainUser, setMainUser] = useState("");
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingSite, setLoadingSite] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [capturingImage, setCapturingImage] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [mapCenter, setMapCenter] = useState([0, 0]);
  const [isValidCoordinates, setIsValidCoordinates] = useState(true);
  const [mapEnabled, setMapEnabled] = useState(false);
  const [capturedBase64, setCapturedBase64] = useState("");
  const [siteImagePreview, setSiteImagePreview] = useState("");
  const [ndviImage, setNdviImage] = useState(null);
  const [ndviImagePreview, setNdviImagePreview] = useState(null);
  const [ndviImageError, setNdviImageError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");

  // Satellite tile layer
  const tileLayer = {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution:
      'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
  };

  // Fetch users and site data
  useEffect(() => {
    if (!id) {
      setError("Invalid site ID. Please select a valid site.");
      setLoadingUsers(false);
      setLoadingSite(false);
      return;
    }

    const fetchData = async () => {
      setLoadingUsers(true);
      setLoadingSite(true);
      setError(null);

      try {
        // Fetch users
        const usersResponse = await axios.get(`${baseURL}/users/`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const userList = usersResponse.data || [];
        // Filter users to include only admins (is_admin: true), superusers (is_superuser: true)
        const filteredUsers = userList.filter(
          (user) => user.is_admin || user.is_superuser
        );
        setUsers(filteredUsers);

        // Fetch site data
        const siteResponse = await axios.get(`${baseURL}/sites/${id}/?with_image=true`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const siteData = siteResponse.data;
        console.log(siteData);
        setSiteName(siteData.name || "");
        setLatitude(siteData.latitude || "");
        setLongitude(siteData.longitude || "");
        setEmailNotifications(siteData.offline_notifications?.enabled || false);
        setMainUser(
          typeof siteData.main_user === "object"
            ? siteData.main_user?.id || ""
            : siteData.main_user || ""
        );
        setMapCenter([parseFloat(siteData.latitude) || 0, parseFloat(siteData.longitude) || 0]);
        setMapEnabled(!!siteData.latitude && !!siteData.longitude);
        setSiteImagePreview(siteData.image || null);

        // Ensure main_user is included in dropdown even if not admin or superuser
        const selectedMainUser =
          typeof siteData.main_user === "object" ? siteData.main_user : null;
        if (selectedMainUser && !filteredUsers.some((user) => user.id === selectedMainUser.id)) {
          setUsers((prevUsers) => [...prevUsers, selectedMainUser]);
        }

        // Fetch NDVI image
        try {
          const ndviResponse = await axios.get(`${baseURL}/ndvi-images/${id}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          setNdviImagePreview(ndviResponse.data.data.image);
        } catch (ndviError) {
          if (ndviError.response?.status === 404) {
            setNdviImagePreview(null); // No NDVI image exists
          } else {
            setError("Failed to load NDVI image.");
          }
        }
      } catch (err) {
        setError("Failed to load site or users. Please try again.");
      } finally {
        setLoadingUsers(false);
        setLoadingSite(false);
      }
    };
    fetchData();
  }, [accessToken, baseURL, id]);

  // Validate and update map center when coordinates change
  useEffect(() => {
    const lat = parseFloat(latitude);
    const lon = parseFloat(longitude);
    const isValidLat = !isNaN(lat) && lat >= -90 && lat <= 90;
    const isValidLon = !isNaN(lon) && lon >= -180 && lon <= 180;
    setIsValidCoordinates(isValidLat && isValidLon);
    if (isValidLat && isValidLon) {
      setMapCenter([lat, lon]);
    }
  }, [latitude, longitude]);

  // Cleanup NDVI image preview URL
  useEffect(() => {
    return () => {
      if (ndviImagePreview && !ndviImagePreview.startsWith("data:")) {
        URL.revokeObjectURL(ndviImagePreview);
      }
    };
  }, [ndviImagePreview]);

  // Handle NDVI image selection
  const handleNdviImageChange = (e) => {
    const file = e.target.files[0];
    setNdviImageError(null);
    if (file) {
      // Validate file type and size
      const allowedTypes = ["image/jpeg", "image/png"];
      if (!allowedTypes.includes(file.type)) {
        setNdviImageError("Only JPG and PNG images are allowed.");
        setNdviImage(null);
        setNdviImagePreview(null);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setNdviImageError("Image must be less than 5MB.");
        setNdviImage(null);
        setNdviImagePreview(null);
        return;
      }
      setNdviImage(file);
      setNdviImagePreview(URL.createObjectURL(file));
    } else {
      setNdviImage(null);
      setNdviImagePreview(null);
    }
  };

  // Capture map as base64 image (336x224 pixels, without marker and labels)
  const captureMapImage = async () => {
    if (!mapRef.current || !isValidCoordinates || !mapEnabled) {
      setError("Cannot capture map image. Ensure valid coordinates and map are enabled.");
      return null;
    }

    setCapturingImage(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      const mapElement = mapRef.current;
      const canvas = await html2canvas(mapElement, {
        useCORS: true,
        allowTaint: true,
        scale: 1,
        width: 336,
        height: 224,
        ignoreElements: (element) =>
          element.classList.contains("leaflet-control-container") ||
          element.classList.contains("leaflet-control") ||
          element.classList.contains("leaflet-marker-pane") ||
          element.classList.contains("leaflet-marker-icon") ||
          element.classList.contains("leaflet-marker-shadow"),
      });
      const base64Image = canvas.toDataURL("image/png");
      setCapturedBase64(base64Image);
      return base64Image;
    } catch (err) {
      setError("Failed to capture map image. Please try again.");
      return null;
    } finally {
      setCapturingImage(false);
    }
  };

  // Map component to update coordinates on click
  const MapUpdater = ({ center, onClick }) => {
    const map = useMap();
    useEffect(() => {
      if (center[0] !== 0 && center[1] !== 0 && mapEnabled) {
        map.setView(center, 15);
      }
    }, [center, map, mapEnabled]);

    useEffect(() => {
      const handleClick = (e) => {
        const { lat, lng } = e.latlng;
        setLatitude(lat.toFixed(6).toString());
        setLongitude(lng.toFixed(6).toString());
        onClick([lat, lng]);
      };
      map.on("click", handleClick);
      return () => map.off("click", handleClick);
    }, [map, onClick]);

    return null;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validate inputs
    if (!siteName.trim()) {
      setError("Site name is required.");
      return;
    }
    if (!latitude || !longitude || !isValidCoordinates) {
      setError("Valid latitude and longitude are required.");
      return;
    }
    const parsedMainUser = Number(mainUser);
    if (!parsedMainUser || !Number.isInteger(parsedMainUser) || parsedMainUser <= 0) {
      setError("A valid main user must be selected.");
      return;
    }
    if (ndviImageError) {
      setError(ndviImageError);
      return;
    }

    // Open confirmation dialog for update
    setDialogAction("update");
    setDialogMessage(`Are you sure you want to update the site "${siteName}"?`);
    setDialogOpen(true);
  };

  // Perform the update action
  const performUpdate = async () => {
    setLoadingSubmit(true);
    try {
      const parsedMainUser = Number(mainUser);
      if (!parsedMainUser || !Number.isInteger(parsedMainUser) || parsedMainUser <= 0) {
        setError("A valid main user must be selected.");
        setLoadingSubmit(false);
        return;
      }

      // Capture map image if not already captured
      let imageToSend = capturedBase64;
      if (!imageToSend) {
        imageToSend = await captureMapImage();
        if (!imageToSend) {
          setLoadingSubmit(false);
          return;
        }
      }

      // Prepare FormData
      const formData = new FormData();
      formData.append("name", siteName);
      formData.append("latitude", latitude.toString());
      formData.append("longitude", longitude.toString());
      formData.append(
        "offline_notifications",
        JSON.stringify({
          emails: [],
          enabled: emailNotifications,
          triggers: [],
        })
      );
      formData.append("main_user", String(parsedMainUser));
      if (imageToSend) {
        formData.append("image", imageToSend);
      }
      if (ndviImage) {
        formData.append("ndvi_image", ndviImage);
      }

      await axios.patch(
        `${baseURL}/sites/${id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "multipart/form-data",
          },
          params: { with_image: imageToSend ? true : null },
        }
      );

      setSuccess("Site updated successfully!");
      // Reset NDVI image fields only
      setNdviImage(null);
      if (ndviImage) {
        setNdviImagePreview(null);
      }
      setCapturedBase64("");
    } catch (err) {
      if (err.response) {
        setError(err.response.data.message || "Failed to update site. Please try again.");
      } else if (err.request) {
        setError("No response from server. Please try again.");
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoadingSubmit(false);
    }
  };

  // Handle site deletion
  const handleDelete = () => {
    setError(null);
    setSuccess(null);
    // Open confirmation dialog for delete
    setDialogAction("delete");
    setDialogMessage(`Are you sure you want to delete the site "${siteName}"? This action cannot be undone.`);
    setDialogOpen(true);
  };

  // Handle dialog confirmation
  const handleConfirmAction = async () => {
    setDialogOpen(false);
    if (dialogAction === "update") {
      await performUpdate();
    } else if (dialogAction === "delete") {
      setLoadingDelete(true);
      try {
        await axios.delete(`${baseURL}/sites/${id}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            accept: "application/json",
          },
        });

        setSuccess("Site deleted successfully!");
        setTimeout(() => navigate("/dashboard/sites"), 1500); // Redirect after brief success message
      } catch (err) {
        if (err.response) {
          setError(err.response.data.message || "Failed to delete site. Please try again.");
        } else if (err.request) {
          setError("No response from server. Please try again.");
        } else {
          setError("An unexpected error occurred. Please try again.");
        }
      } finally {
        setLoadingDelete(false);
      }
    }
  };

  // Handle dialog cancellation
  const handleCancelAction = () => {
    setDialogOpen(false);
    setDialogAction("");
    setDialogMessage("");
  };

  // Get user display name
  const getUserDisplayName = (user) => {
    const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
    return fullName || user.username || `User ${user.id}`;
  };

  // Redirect or show error if id is invalid
  if (!id) {
    return (
      <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3 } }}>
        <Alert severity="error">
          Invalid site ID. Please select a valid site or return to the site list.
        </Alert>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/sites")}
          sx={{ mt: 2 }}
        >
          Back to Sites
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: "1.75rem", md: "2.125rem" } }}>
        Edit Site
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      {capturedBase64 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Map image captured successfully (336x224 base64 string ready for submission).
        </Alert>
      )}
      {(loadingSite || loadingUsers) && (
        <LoadingPage/>
      )}
      {!loadingSite && !loadingUsers && (
        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Site Name */}
            <TextField
              label="Site Name"
              value={siteName}
              onChange={(e) => setSiteName(e.target.value)}
              fullWidth
              required
              variant="outlined"
            />
            {/* Location */}
            <Typography variant="h6" sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
              Location
            </Typography>
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                label="Latitude"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
                fullWidth
                type="text"
                required
                error={!isValidCoordinates && latitude !== ""}
                helperText={
                  !isValidCoordinates && latitude !== "" ? "Latitude must be between -90 and 90" : ""
                }
              />
              <TextField
                label="Longitude"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
                fullWidth
                type="text"
                required
                error={!isValidCoordinates && longitude !== ""}
                helperText={
                  !isValidCoordinates && longitude !== "" ? "Longitude must be between -180 and 180" : ""
                }
              />
            </Box>

            {/* Map Section */}
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box sx={{ height: 224, width: 336, mb: 2 }}>
                <Typography variant="h6" sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" }, mb: 1 }}>
                  Location Map
                </Typography>
                {mapEnabled && isValidCoordinates && latitude && longitude ? (
                  <div ref={mapRef} style={{ height: "100%", width: "100%" }}>
                    <MapContainer
                      center={mapCenter}
                      zoom={15}
                      style={{ height: "100%", width: "100%" }}
                      zoomControl={!capturingImage}
                      attributionControl={!capturingImage}
                    >
                      <TileLayer url={tileLayer.url} attribution={capturingImage ? "" : tileLayer.attribution} />
                      {!capturingImage && (
                        <TileLayer
                          url="https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                          attribution=""
                        />
                      )}
                      {!capturingImage && <Marker position={mapCenter} />}
                      <MapUpdater center={mapCenter} onClick={setMapCenter} />
                    </MapContainer>
                  </div>
                ) : (
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "grey.100",
                      cursor: "pointer",
                    }}
                    onClick={() => setMapEnabled(true)}
                  >
                    <Typography color="text.secondary">
                      {latitude && longitude ? "Click the map to set coordinates" : "Click to enable map"}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" }, mb: 1 }}>
                  Current Site Image
                </Typography>
                <SiteImage
                  src={siteImagePreview}
                  alt="Site Image Preview"
                  sx={{
                    maxWidth: "100%",
                    maxHeight: 280,
                    height: "auto",
                    borderRadius: "4px",
                  }}
                />
              </Box>
            </Box>
            
            {/* NDVI Image Upload */}
            <Box>
              <Typography variant="h6" sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" }, mb: 1 }}>
                NDVI Image (Optional)
              </Typography>
              <Button
                variant="contained"
                component="label"
                sx={{ mb: 2 }}
              >
                Upload NDVI Image
                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/png"
                  onChange={handleNdviImageChange}
                />
              </Button>
              {ndviImageError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {ndviImageError}
                </Alert>
              )}
              {ndviImagePreview && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Preview:
                  </Typography>
                  <img
                    src={ndviImagePreview}
                    alt="NDVI Preview"
                    style={{ maxWidth: "200px", maxHeight: "200px", borderRadius: "4px" }}
                  />
                </Box>
              )}
              {!ndviImagePreview && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h5" color="primary" sx={{ mb: 1 }}>
                    NDVI Image not available
                  </Typography>
                </Box>
              )}
            </Box>
            {/* Email Notifications */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={emailNotifications}
                  onChange={(e) => setEmailNotifications(e.target.checked)}
                />
              }
              label="Send email notifications"
            />
            {/* Main User */}
            <FormControl fullWidth variant="outlined" disabled={loadingUsers}>
              <InputLabel id="main-user-select-label">Main User</InputLabel>
              <Select
                labelId="main-user-select-label"
                value={mainUser}
                onChange={(e) => setMainUser(e.target.value)}
                label="Main User"
                required
              >
                {loadingUsers ? (
                  <MenuItem value="" disabled>
                    Loading users...
                  </MenuItem>
                ) : users.length === 0 ? (
                  <MenuItem value="" disabled>
                    No users available
                  </MenuItem>
                ) : (
                  users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {getUserDisplayName(user)}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            {/* Action Buttons */}
            <Box sx={{ display: "flex", gap: 2, mt: 2, alignSelf: "flex-end" }}>
              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
                disabled={loadingDelete || loadingUsers || loadingSite || capturingImage || loadingSubmit}
                sx={{ minWidth: 120 }}
              >
                {loadingDelete ? <CircularProgress size={24} /> : "Delete Site"}
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loadingSubmit || loadingUsers || loadingSite || capturingImage || ndviImageError || loadingDelete}
                sx={{ minWidth: 120 }}
              >
                {loadingSubmit ? <CircularProgress size={24} /> : "Update Site"}
              </Button>
            </Box>
          </Box>
        </Paper>
      )}
      {/* Confirmation Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCancelAction}
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            sx={{ color: "black" }}
          >
            {dialogMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAction} color="primary">
            No
          </Button>
          <Button onClick={handleConfirmAction} color="error" autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EditSite;
